"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Building2, Star, Code as Code2, Cpu, FileSearch, Globe, Globe as Globe2, TrendingUp, ShoppingBag, Sparkles, Layers, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
const servicesData = [{
  id: "gbp-opt",
  title: "Google Business Profile Optimization",
  category: "Local SEO",
  description: "Transform your Google Business Profile into a lead magnet. I optimize your listing for max visibility in the local 3-pack, driving phone calls and driving directions.",
  path: "/LocalSEOPage",
  badge: "High Impact",
  iconName: "MapPin"
}, {
  id: "citation-building",
  title: "Local Citation Building & Audit",
  category: "Local SEO",
  description: "Establish absolute geographic trust with search engines. I clean up existing NAP inconsistencies and build structured local directory listings on authority sites.",
  path: "/LocalSEOPage",
  badge: "Foundational",
  iconName: "Building2"
}, {
  id: "review-management",
  title: "Review Management Strategy",
  category: "Reputation",
  description: "Implement compliant customer feedback loops that earn consistent 5-star ratings, boosting local trust signals and organic customer conversions.",
  path: "/LocalSEOPage",
  badge: "Trust Factor",
  iconName: "Star"
}, {
  id: "schema-markup",
  title: "Local & Structured Schema Markup",
  category: "Technical",
  description: "Deploy custom JSON-LD structured data for local business entities, services, reviews, and address details to unlock rich snippet results.",
  path: "/TechnicalSeoPage",
  badge: "Technical",
  iconName: "Code2"
}, {
  id: "technical-seo",
  title: "Technical SEO & Web Vitals",
  category: "Technical",
  description: "Eliminate crawl errors, optimize render trees, and accelerate page speeds so Google's bots crawl and index your revenue pages efficiently.",
  path: "/TechnicalSeoPage",
  badge: "Core Stack",
  iconName: "Cpu"
}, {
  id: "seo-audits",
  title: "Comprehensive SEO Audits",
  category: "Diagnostics",
  description: "A deep-dive, hands-on audit identifying technical bottlenecks, content gaps, and toxic link risks with a clear prioritized roadmap.",
  path: "/SeoAuditsPage",
  badge: "Diagnostic",
  iconName: "FileSearch"
}, {
  id: "national-seo",
  title: "National Organic Search Strategy",
  category: "Growth",
  description: "Expand beyond local borders. I craft long-term organic strategies that position your brand at the top of high-intent competitive keywords nationwide.",
  path: "/NationalSeoPage",
  badge: "Scale Up",
  iconName: "Globe"
}, {
  id: "international-seo",
  title: "International & Multi-Region SEO",
  category: "Global",
  description: "Target overseas audiences with precise hreflang implementation, localized keyword intent mapping, and multi-regional domain architecture.",
  path: "/InternationalSeoPage",
  badge: "Global",
  iconName: "Globe2"
}, {
  id: "b2b-lead-gen",
  title: "B2B SEO & Organic Lead Gen",
  category: "B2B Services",
  description: "Capture high-value enterprise prospects. I target commercial search intent to consistently fuel your sales funnel with qualified business inquiries.",
  path: "/B2BSeoLeadGenerationPage",
  badge: "B2B Focus",
  iconName: "TrendingUp"
}, {
  id: "ecommerce-seo",
  title: "Ecommerce Product SEO",
  category: "Ecommerce",
  description: "Optimize product categories, faceted navigation, and transactional pages to capture buyer search traffic and boost store revenue.",
  path: "/EcommerceSeoPage",
  badge: "Revenue Drive",
  iconName: "ShoppingBag"
}, {
  id: "ai-search-opt",
  title: "AI Search & Generative Engine (GEO)",
  category: "Modern SEO",
  description: "Future-proof your organic visibility across AI search assistants and ChatGPT by structuring entity data for conversational search engines.",
  path: "/AISearchOptimisationPage",
  badge: "Next-Gen",
  iconName: "Sparkles"
}, {
  id: "content-architecture",
  title: "Keyword & Content Architecture",
  category: "Strategy",
  description: "Map out topic clusters and editorial briefs that establish topical authority in your niche, positioning you as the trusted industry expert.",
  path: "/FreelanceSeoConsultantPage",
  badge: "Strategic",
  iconName: "Layers"
}];
const renderIcon = (iconName: string) => {
  const iconProps = {
    className: "size-5 text-primary-foreground"
  };
  switch (iconName) {
    case "MapPin":
      return <MapPin {...iconProps} />;
    case "Building2":
      return <Building2 {...iconProps} />;
    case "Star":
      return <Star {...iconProps} />;
    case "Code2":
      return <Code2 {...iconProps} />;
    case "Cpu":
      return <Cpu {...iconProps} />;
    case "FileSearch":
      return <FileSearch {...iconProps} />;
    case "Globe":
      return <Globe {...iconProps} />;
    case "Globe2":
      return <Globe2 {...iconProps} />;
    case "TrendingUp":
      return <TrendingUp {...iconProps} />;
    case "ShoppingBag":
      return <ShoppingBag {...iconProps} />;
    case "Sparkles":
      return <Sparkles {...iconProps} />;
    case "Layers":
      return <Layers {...iconProps} />;
    default:
      return <CheckCircle2 {...iconProps} />;
  }
};
export default function Services() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="servicesgrid" data-nav="light" className="bg-background text-foreground relative py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mx-auto max-w-3xl text-center">
          <Badge variant="secondary" className="mb-4 bg-secondary text-secondary-foreground font-medium px-3 py-1">
            Direct Senior Expertise
          </Badge>
          <h2 className="font-default text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
            Tailored SEO Services Delivered Directly By Me
          </h2>
          <p className="text-muted-foreground mt-4 text-base sm:text-lg leading-relaxed">
            No account managers, no junior hand-offs. Every strategy, audit, and technical deployment is executed directly by Yassine Essaadi for maximum accountability and organic growth.
          </p>
        </div>

        {/* Services Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        margin: "-50px"
      }} className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {servicesData.map((service, index) => <motion.div key={service.id} variants={cardVariants} data-index={index}>
              <Card className="border-border bg-card shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-md flex h-full flex-col overflow-hidden rounded-xl">
                {/* Header Strip */}
                <div className="bg-primary text-primary-foreground p-5 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-primary-foreground/15 rounded-lg p-2.5">
                      {renderIcon(service.iconName)}
                    </div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-primary-foreground/90">
                      {service.category}
                    </span>
                  </div>
                  <Badge variant="outline" className="border-primary-foreground/30 text-primary-foreground bg-primary-foreground/10 text-[11.5px] font-normal">
                    {service.badge}
                  </Badge>
                </div>

                <CardHeader className="pt-5 pb-2">
                  <CardTitle className="font-default text-lg font-semibold leading-snug">
                    {service.title}
                  </CardTitle>
                </CardHeader>

                <CardContent className="flex-1 pb-4">
                  <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                    {service.description}
                  </CardDescription>
                </CardContent>

                <CardFooter className="pt-2 pb-5">
                  <Button asChild variant="ghost" size="sm" className="text-primary hover:text-primary hover:bg-muted w-full justify-between font-medium group">
                    <Link to={service.path}>
                      <span>Explore Details</span>
                      <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Bottom CTA Card */}
        <div className="mt-16 bg-muted border-border rounded-2xl p-6 sm:p-8 md:p-10 text-center border">
          <div className="mx-auto max-w-2xl">
            <h3 className="font-default text-2xl font-semibold text-foreground">
              Not sure which SEO strategy fits your current growth stage?
            </h3>
            <p className="text-muted-foreground mt-2 text-sm sm:text-base">
              Book a complimentary SEO consultation directly with Yassine to analyze your domain, identify technical quick wins, and outline a custom search roadmap.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center items-center">
              <Button asChild size="lg" className="w-full sm:w-auto font-medium">
                <Link to="/ContactPage">Book a Free SEO Consultation</Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-medium">
                <Link to="/CaseStudiesPage">Explore Case Studies</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}